import React, { useState, useRef } from 'react';
import html2canvas from 'html2canvas';
import { ARCHETYPE_META, ArchetypeIcon } from './NeuralProfile';
import ShareImageCard from './ShareImageCard';
import ArchetypeShareCard from './ArchetypeShareCard';

const DIMENSION_SHORT = {
  rawSpeed:         'Speed',
  decisionQuality:  'Decision',
  aimPrecision:     'Aim',
  trackingAccuracy: 'Tracking',
  workingMemory:    'Memory',
  consistency:      'Consistency',
  flickSpeed:       'Flick',
  prioritization:   'Prioritization',
};

/**
 * ProfileCard — a compact shareable summary card.
 * Displayed in a modal on the profile page.
 */
export default function ProfileCard({ profile }) {
  const cardRef = useRef(null);
  const shareImageRef = useRef(null);
  const archetypeShareRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingArchetype, setDownloadingArchetype] = useState(false);
  const [challengeCopied, setChallengeCopied] = useState(false);
  const meta = ARCHETYPE_META[profile.archetype.id] || { color: '#8b9ab5', icon: '◈' };
  const { scores } = profile;

  const dimEntries = Object.entries(scores).filter(([k]) => k !== 'overallScore');
  const overallScore = scores.overallScore !== undefined
    ? scores.overallScore
    : Math.round(dimEntries.reduce((a, [, v]) => a + v, 0) / dimEntries.length);

  const profileUrl   = `${window.location.origin}/profile/${profile.shareableId}`;
  const challengeUrl = `${window.location.origin}/challenge/${profile.shareableId}`;

  const copyChallenge = () => {
    const challengeText = `I scored ${overallScore}/100 as a ${profile.archetype.name} on NeuroRank. Can you beat it? ${challengeUrl}`;
    navigator.clipboard.writeText(challengeText).then(() => {
      setChallengeCopied(true);
      setTimeout(() => setChallengeCopied(false), 2000);
    });
  };

  const copyLink = () => {
    navigator.clipboard.writeText(profileUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const tweetText = `I scored ${overallScore}/100 as a ${profile.archetype.name} on the NeuroRank Cognitive Combine. Check my neural profile:`;
  const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(profileUrl)}`;

  const discordText = `**NeuroRank — ${profile.archetype.name}** · ${overallScore}/100\n${profileUrl}`;
  const copyDiscord = () => {
    navigator.clipboard.writeText(discordText).then(() => {
      setCopied('discord');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDownloadImage = async () => {
    setDownloading(true);
    try {
      const canvas = await html2canvas(shareImageRef.current, {
        scale: 1,
        useCORS: true,
        backgroundColor: '#0d1421',
        width: 1200,
        height: 630,
        logging: false,
      });
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = `neurorank-${profile.shareableId}.png`;
      a.click();
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadArchetypeCard = async () => {
    setDownloadingArchetype(true);
    try {
      const canvas = await html2canvas(archetypeShareRef.current, {
        scale: 1,
        useCORS: true,
        backgroundColor: '#06090f',
        width: 1200,
        height: 630,
        logging: false,
      });
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = `neurorank-archetype-${profile.shareableId}.png`;
      a.click();
    } finally {
      setDownloadingArchetype(false);
    }
  };

  const topDimensions = dimEntries.sort(([, a], [, b]) => b - a).slice(0, 3);

  const genreColor = { general: '#00d4ff', fps: '#ef4444', moba: '#a855f7' }[profile.genre] || '#00d4ff';

  return (
    <>
    <div
      ref={cardRef}
      style={{
        background: '#0d1421',
        border: `2px solid ${meta.color}44`,
        borderRadius: 16,
        padding: 28,
        width: 360,
        boxShadow: `0 0 40px ${meta.color}22`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Glow corner */}
      <div style={{
        position: 'absolute', top: 0, right: 0, width: 120, height: 120,
        background: `radial-gradient(circle at top right, ${meta.color}20, transparent 70%)`,
        pointerEvents: 'none',
      }} />

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', letterSpacing: 2, marginBottom: 4 }}>
            NEURORANK COMBINE
          </div>
          <h2 style={{ fontSize: 22, marginBottom: 6 }}>{profile.playerTag}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: `${meta.color}18`, border: `1px solid ${meta.color}44`,
              borderRadius: 20, padding: '3px 10px',
              fontSize: 12, fontWeight: 600, color: meta.color,
            }}>
              <ArchetypeIcon archetypeId={profile.archetype.id} size={13} /> {profile.archetype.name}
            </div>
            {profile.genre && (
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: 1.2,
                padding: '3px 8px', borderRadius: 20,
                border: `1px solid ${genreColor}44`, color: genreColor,
                textTransform: 'uppercase',
              }}>
                {profile.genre}
              </span>
            )}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>OVERALL</div>
          <div style={{ fontSize: 40, fontWeight: 900, color: meta.color, lineHeight: 1 }}>{overallScore}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>/100</div>
        </div>
      </div>

      {/* Top 3 Dimensions */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', letterSpacing: 2, marginBottom: 10 }}>
          TOP COGNITIVE ATTRIBUTES
        </div>
        {topDimensions.map(([key, val]) => (
          <div key={key} style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{DIMENSION_SHORT[key] || key}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>{val}</span>
            </div>
            <div style={{ height: 3, background: 'var(--border)', borderRadius: 2 }}>
              <div style={{
                height: '100%', width: `${val}%`, borderRadius: 2,
                background: meta.color, boxShadow: `0 0 6px ${meta.color}88`,
              }} />
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{
        borderTop: '1px solid var(--border)', paddingTop: 14,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>
            {profile.shareableId}
          </span>
          <button
            className="btn btn--primary"
            style={{ padding: '6px 16px', fontSize: 12 }}
            onClick={copyLink}
          >
            {copied === true ? '✓ Copied!' : 'Copy Link'}
          </button>
        </div>
        <button
          className="btn btn--primary"
          style={{ width: '100%', marginBottom: 8, fontSize: 12 }}
          onClick={handleDownloadImage}
          disabled={downloading}
        >
          {downloading ? 'Generating...' : 'Download Score Card'}
        </button>
        <button
          className="btn btn--primary"
          style={{ width: '100%', marginBottom: 8, fontSize: 12 }}
          onClick={handleDownloadArchetypeCard}
          disabled={downloadingArchetype}
        >
          {downloadingArchetype ? 'Generating...' : 'Download Archetype Card'}
        </button>
        <button
          style={{
            width: '100%', marginBottom: 8, padding: '9px 0',
            fontSize: 12, fontWeight: 700, cursor: 'pointer', borderRadius: 8,
            background: challengeCopied ? `${meta.color}22` : 'transparent',
            border: `1px solid ${meta.color}55`, color: meta.color,
            transition: 'all 0.15s',
          }}
          onClick={copyChallenge}
        >
          {challengeCopied ? '✓ Challenge Copied!' : '⚔ Challenge a Friend'}
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          <a
            href={tweetUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              flex: 1, textAlign: 'center', padding: '7px 0',
              fontSize: 12, fontWeight: 600, color: '#1d9bf0',
              border: '1px solid #1d9bf033', borderRadius: 8,
              background: '#1d9bf011',
              textDecoration: 'none',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#1d9bf022'}
            onMouseLeave={e => e.currentTarget.style.background = '#1d9bf011'}
          >
            Share on X
          </a>
          <button
            onClick={copyDiscord}
            style={{
              flex: 1, padding: '7px 0',
              fontSize: 12, fontWeight: 600, color: '#5865f2',
              border: '1px solid #5865f233', borderRadius: 8,
              background: copied === 'discord' ? '#5865f222' : '#5865f211',
              cursor: 'pointer',
              transition: 'background 0.15s',
            }}
          >
            {copied === 'discord' ? '✓ Copied!' : 'Copy for Discord'}
          </button>
        </div>
      </div>
    </div>
    <ShareImageCard profile={profile} shareImageRef={shareImageRef} />
    <ArchetypeShareCard profile={profile} archetypeShareRef={archetypeShareRef} />
    </>
  );
}
